/**
 * Mobile browsers block programmatic audio playback (`Audio.play()`,
 * `speechSynthesis.speak()`) unless the call originates from a user-gesture
 * event handler.  This module provides a one-shot unlock that should be called
 * on the first user tap/click inside the verbal session.  Once unlocked, later
 * programmatic playback via the shared AudioContext succeeds.
 */

let sharedCtx: AudioContext | null = null;
let unlocked = false;

/** Returns `true` once `ensureAudioUnlocked` has run successfully. */
export function isAudioUnlocked(): boolean {
  return unlocked;
}

/** Shared AudioContext created during unlock.  May be `null` before first gesture. */
export function getSharedAudioContext(): AudioContext | null {
  return sharedCtx;
}

/**
 * Call from any user-gesture handler (click, touchstart, keydown) to prime
 * the browser for later programmatic playback.
 *
 * Safe to call repeatedly — it only does real work once.
 */
export function ensureAudioUnlocked(): void {
  if (unlocked) return;
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = sharedCtx ?? new Ctx();
    sharedCtx = ctx;

    if (ctx.state === 'suspended') {
      void ctx.resume();
    }

    // Play a tiny silent buffer to satisfy the "user activated playback" requirement.
    const buf = ctx.createBuffer(1, 1, ctx.sampleRate);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start();

    unlocked = true;
  } catch {
    // Best-effort; if AudioContext isn't available, we fall through.
  }
}

/**
 * Play raw audio bytes (e.g. an MP3 response body) through the shared
 * AudioContext.  Returns a promise that resolves when playback ends.
 *
 * This works on mobile even when `HTMLAudioElement.play()` is blocked,
 * as long as `ensureAudioUnlocked()` was called during a prior user gesture.
 */
export async function playThroughAudioContext(
  audioData: ArrayBuffer,
  onStart?: () => void,
  onEnd?: () => void,
): Promise<{ stop: () => void }> {
  const ctx = sharedCtx;
  if (!ctx) throw new Error('AudioContext not initialised');

  if (ctx.state === 'suspended') {
    await ctx.resume();
  }

  const decoded = await ctx.decodeAudioData(audioData.slice(0));
  const source = ctx.createBufferSource();
  source.buffer = decoded;
  source.connect(ctx.destination);

  return new Promise((resolve) => {
    source.onended = () => {
      onEnd?.();
      resolve({ stop() {} });
    };
    onStart?.();
    source.start();
    resolve({
      stop() {
        try {
          source.stop();
        } catch {
          /* already stopped */
        }
        onEnd?.();
      },
    });
  });
}
